import { subject } from '@casl/ability'
import { sizes } from '@lmc/common/constants'
import { type Carer, type Home, UserRole } from '@lmc/ts-common'
import { EmptyState, NoDataIcon } from '@lmc/ui-kit'
import { useAbilityAccess } from 'components/AccessControl'
import { canViewerEditAccessAndPermissionsOnTarget } from 'components/forms/screens/TeamMemberEditForm/utils'
import { AddTeamMemberWizard } from 'components/modals/AddTeamMemberWizard'
import useHomesList from 'hooks/useHome/useHomes'
import { NAMESPACE } from 'locale'
import { reportsConstants } from 'locale/team'
import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { Spinner } from '../../../../components/generic'
import useCarers from '../../hooks/useCarers'
import usePagedCarers from '../../hooks/usePagedCarers'
import {
  carersForTab,
  isListableCarer,
  isRoleTabId,
  RoleTabId
} from '../../utils/roleTabs'
import { getSiteNames } from '../../utils/siteAccess'
import { DEFAULT_SORT, type SortKey, sortCarers } from '../../utils/sort'
import {
  applyTeamFilters,
  countChangedFilters,
  DEFAULT_FILTERS,
  type TeamFilters
} from '../../utils/teamFilters'
import { TEAM_LIST_QUERY_KEYS } from './constants'
import DeleteAccountModal from './DeleteAccountModal'
import EditPermissionsPanel from './EditPermissionsPanel'
import RoleTabs from './RoleTabs'
import TeamFiltersButton from './TeamFiltersButton'
import TeamFiltersPanel from './TeamFiltersPanel'
import TeamListHeaderV2 from './TeamListHeaderV2'
import TeamListItemV2 from './TeamListItemV2'
import TeamListPagination from './TeamListPagination'
import TeamListToolbar from './TeamListToolbar'

const StyledScreen = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 109px);
  background-color: ${(props) => props.theme.color.background.tertiary};
  padding: 24px 0;
  overflow-y: auto;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    min-height: calc(100vh - 63px);
    padding: 16px 0;
  }
`

const StyledContent = styled.div`
  width: 100%;
  max-width: ${sizes.childScreen}px;
  margin: 0 auto;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    padding: 0 16px;
    gap: 16px;
  }
`

const StyledMainCard = styled.div`
  background-color: ${(props) => props.theme.palette.white};
  border-radius: 16px;
  overflow: hidden;
`

const StyledList = styled.ul`
  background-color: ${(props) => props.theme.palette.white};
  list-style: none;
  margin: 0;
  padding: 0;
`

const StyledEmpty = styled.div`
  padding: 48px 24px;
`

const matchesName = (carer: Carer, search: string): boolean => {
  if (!search) {
    return true
  }
  const haystack =
    `${carer?.name?.first ?? ''} ${carer?.name?.last ?? ''}`.toLowerCase()
  return haystack.includes(search.toLowerCase())
}

// Always true for the group screen — it's an independent route only mounted
// for group users (see TeamListGroupsRedirect).
const GROUP_USER = true

const TeamListGroupsV2: React.FC = () => {
  const { t } = useTranslation()
  const { t: tTeam } = useTranslation(NAMESPACE.TEAM)
  const queryClient = useQueryClient()
  const { isLoading, carers, fetchCarers } = useCarers()
  const location = useLocation()
  const navigate = useNavigate()

  // Group homes list, used to resolve each carer's site-access names. Resolve
  // names off the Redux `state.data.homes` reference list when present, else the
  // live query. The query's loading state gates the screen (below) so the
  // site-access column and wizard Site picker don't render empty on first paint.
  const { homes: groupHomes, isLoading: isLoadingHomes } = useHomesList({
    params: { isGroupUserManagement: GROUP_USER }
  })
  const reduxHomes = useSelector(
    (state: { data: { homes: Home[] } }) => state.data.homes
  )
  const viewerRole = useSelector(
    (state: { global: { user?: { role?: UserRole } } }) =>
      state.global.user?.role
  )
  const homes = reduxHomes?.length ? reduxHomes : groupHomes

  // Check update User ability, but also allow management role even if ability check fails (CM-3762)
  const abilityResult = useAbilityAccess([{ i: 'update', this: 'User' }])
  const canManageUsers =
    abilityResult ||
    viewerRole === UserRole.careHomeAdmin ||
    viewerRole === UserRole.management
  // Delete is a distinct ability from edit, so a viewer who can edit permissions
  // but not delete accounts must not see the Delete action (matches legacy).
  const canDeleteUser = useAbilityAccess([{ i: 'delete', this: 'User' }])
  // Probe against a staff-role invitation, not the bare 'UserInvitation' type:
  // Senior Carers have a conditional `create UserInvitation where role: family`
  // rule, and a bare-type check matches it. A role-scoped subject only passes
  // for roles that can actually invite staff (Admin / Management / group roles).
  const canInviteUser = useAbilityAccess([
    { i: 'create', this: subject('UserInvitation', { role: UserRole.carer }) }
  ])

  const tabFromUrl = new URLSearchParams(location.search).get('tab')
  const activeTab: RoleTabId = isRoleTabId(tabFromUrl, true)
    ? tabFromUrl
    : RoleTabId.AllMembers

  // Clean up unrecognised `?tab=` params from the URL so they don't survive
  // navigation. Runs once per unrecognised value.
  useEffect(() => {
    if (tabFromUrl !== null && !isRoleTabId(tabFromUrl, true)) {
      const params = new URLSearchParams(location.search)
      params.delete('tab')
      navigate(
        { pathname: location.pathname, search: params.toString() },
        { replace: true }
      )
    }
  }, [tabFromUrl, location.pathname, location.search, navigate])

  const [search, setSearch] = useState<string>('')
  const [sortKey, setSortKey] = useState<SortKey>(DEFAULT_SORT)
  const [isWizardOpen, setIsWizardOpen] = useState(false)
  // CM-3388: the group screen's Add-member dropdown opens the wizard in one of
  // two scopes — site-scoped invite vs group-dashboard invite.
  const [wizardScope, setWizardScope] = useState<
    'specific-sites' | 'group-dashboard'
  >('specific-sites')
  // The Edit-permissions panel mounts only while a carer is selected; clearing
  // the carer on close discards any unsaved edits so reopening starts fresh.
  const [editCarer, setEditCarer] = useState<Carer | null>(null)
  // The delete-account modal mounts only while a carer is selected for deletion;
  // clearing it on close resets the type-to-confirm input.
  const [deleteCarer, setDeleteCarer] = useState<Carer | null>(null)
  const [filters, setFilters] = useState<TeamFilters>(DEFAULT_FILTERS)
  const [isFiltersOpen, setIsFiltersOpen] = useState(false)

  // Search is intentionally available only on the 'All members' tab.
  useEffect(() => {
    if (activeTab !== RoleTabId.AllMembers && search !== '') {
      setSearch('')
    }
  }, [activeTab, search])

  const handleTabChange = (next: RoleTabId) => {
    const params = new URLSearchParams(location.search)
    if (next === RoleTabId.AllMembers) {
      params.delete('tab')
    } else {
      params.set('tab', next)
    }
    navigate({ pathname: location.pathname, search: params.toString() })
  }

  const listableCarers = useMemo(
    () => (carers ?? []).filter(isListableCarer),
    [carers]
  )

  const filteredCarers = useMemo(() => {
    const byTab = carersForTab(listableCarers, activeTab)
    const effectiveSearch = activeTab === RoleTabId.AllMembers ? search : ''
    const bySearch = byTab.filter((c) => matchesName(c, effectiveSearch))
    const byFilters = applyTeamFilters(bySearch, filters)
    return sortCarers(byFilters, sortKey)
  }, [listableCarers, activeTab, search, sortKey, filters])

  const resetKey = `${activeTab}|${search}|${sortKey}|${filters.statuses.join(
    ','
  )}|${filters.siteIds.join(',')}`
  const { pagedCarers, currentPage, totalPages, setPage } = usePagedCarers(
    filteredCarers,
    resetKey
  )

  const openWizardWithScope = (scope: 'specific-sites' | 'group-dashboard') => {
    setWizardScope(scope)
    setIsWizardOpen(true)
  }

  const handleEditPermissions = (carer: Carer) => setEditCarer(carer)

  const handleSeeProfile = (carer: Carer) => {
    if (!carer?.id) {
      return
    }
    navigate(`/organisation/team/${carer.id}/profile`)
  }

  const handleDeleteAccount = (carer: Carer) => setDeleteCarer(carer)

  // Wait for the homes reference list too when we depend on the live query
  // (Redux has none yet) — otherwise site names / the Site picker flash empty.
  if (isLoading || (!reduxHomes?.length && isLoadingHomes)) {
    return <Spinner />
  }

  return (
    <StyledScreen data-testid="team-list-groups-v2">
      <StyledContent>
        <StyledMainCard>
          <TeamListHeaderV2
            canAddMember={canInviteUser}
            addMemberOptions={[
              {
                key: 'specific-sites',
                label: tTeam(reportsConstants.TEAM_LIST_ADD_TO_SPECIFIC_SITES),
                onSelect: () => openWizardWithScope('specific-sites')
              },
              {
                key: 'group-dashboard',
                label: tTeam(reportsConstants.TEAM_LIST_ADD_TO_GROUP_DASHBOARD),
                onSelect: () => openWizardWithScope('group-dashboard')
              }
            ]}
            testId="team-list-groups-v2-header"
          />
        </StyledMainCard>
        <StyledMainCard>
          <RoleTabs
            activeTab={activeTab}
            onChange={handleTabChange}
            includeGroupRoles
            testId="team-list-groups-v2-tabs"
          />
          <TeamListToolbar
            searchValue={search}
            onSearchChange={setSearch}
            searchVisible={activeTab === RoleTabId.AllMembers}
            filtersControl={
              <TeamFiltersButton
                activeCount={countChangedFilters(filters)}
                showCount
                onClick={() => setIsFiltersOpen(true)}
                testId="team-list-groups-v2-toolbar-filters"
              />
            }
            sortKey={sortKey}
            onSortChange={setSortKey}
            activeTab={activeTab}
            count={filteredCarers.length}
            testId="team-list-groups-v2-toolbar"
          />
          {pagedCarers.length ? (
            <StyledList
              role="list"
              aria-label="Team members"
              data-testid="team-list-groups-v2-list"
            >
              {pagedCarers.map((carer) => (
                <TeamListItemV2
                  key={carer.id}
                  testId="team-list-groups-v2-row"
                  carer={carer}
                  variant="group"
                  siteNames={getSiteNames(carer, homes)}
                  canEditPermissions={canManageUsers}
                  canEditTarget={canViewerEditAccessAndPermissionsOnTarget(
                    viewerRole,
                    carer.role as UserRole
                  )}
                  canDeleteAccount={canDeleteUser}
                  onEditPermissions={handleEditPermissions}
                  onDeleteAccount={handleDeleteAccount}
                  onSeeProfile={handleSeeProfile}
                />
              ))}
            </StyledList>
          ) : listableCarers.length === 0 ? (
            <StyledEmpty>
              <EmptyState
                title={t('general.emptyState.noTeamListResponseTitle')}
                description={t(
                  'general.emptyState.noTeamListResponseDescription'
                )}
                icon={<NoDataIcon />}
                testId="team-list-groups-v2-empty"
              />
            </StyledEmpty>
          ) : (
            <StyledEmpty>
              <EmptyState
                title={t('general.emptyState.noTeamMembersMatchTitle')}
                description={t(
                  'general.emptyState.noTeamMembersMatchDescription'
                )}
                icon={<NoDataIcon />}
                testId="team-list-groups-v2-empty"
              />
            </StyledEmpty>
          )}
          <TeamListPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setPage}
            testId="team-list-groups-v2-pagination"
          />
        </StyledMainCard>
      </StyledContent>
      <AddTeamMemberWizard
        open={isWizardOpen}
        onClose={() => {
          setIsWizardOpen(false)
          // Invalidate and refetch so a newly-invited member appears without a manual reload.
          // The wizard only calls onClose on cancel or after a successful invite;
          // invalidation clears React Query cache (CM-3785).
          queryClient.invalidateQueries(TEAM_LIST_QUERY_KEYS.CARERS)
        }}
        scope={wizardScope}
        homes={homes}
        testId="add-team-member-wizard"
      />
      <EditPermissionsPanel
        open={editCarer !== null}
        carer={editCarer}
        onClose={() => setEditCarer(null)}
        onSaved={fetchCarers}
      />
      <TeamFiltersPanel
        open={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        filters={filters}
        onApply={setFilters}
        homes={homes}
        testId="team-list-groups-v2-filters-panel"
      />
      <DeleteAccountModal
        open={deleteCarer !== null}
        carer={deleteCarer}
        onClose={() => setDeleteCarer(null)}
        onDeleted={fetchCarers}
      />
    </StyledScreen>
  )
}

export default TeamListGroupsV2
